import { z } from "zod";

/**
 * Leads / Profiles / Interests shared contract (CRM demand surface, Epic L / S4).
 *
 * Single source of truth for the Lead/LeadProfile/Interest wire shape shared by
 * the web SPA (`src/repositories/leads.ts`) and the NestJS slice
 * (`apps/api/src/leads/*`, `apps/api/src/interests/*`). Like brokerage this is a
 * **Prisma-first** domain — NestJS is the system of record. The shape is
 * **snake_case** so the FE<->Nest transport is a field-for-field swap and the FE
 * `.parse()`s Nest responses against {@link leadSchema} / {@link interestSchema}
 * at the flip boundary (drift surfaces at runtime).
 *
 * TOLERANCE RULE (the #1699 silent-no-op lesson): own columns are pinned, but
 * the row schemas are `.passthrough()` so a new server column never rejects a
 * valid payload, and status/enum fields stay OPEN STRINGS on the row so a new
 * enum value never makes the FE silently reject server data (the exact policy
 * the brokerage + service-requests row schemas follow).
 *
 * NULLABILITY RULE: required core columns are pinned; EVERY other column is
 * uniformly `.nullish()` — null and absent are equivalent on the wire (the
 * server serializes null, the FE sends null for cleared fields, and an older API
 * omitting a newer column must never reject). Don't mix `.nullable()` /
 * `.optional()` per-field.
 *
 * ENUM LOCKSTEP (acceptance criterion): the standalone exported enum schemas
 * below MUST match the Prisma enums in `apps/api/prisma/schema.prisma` EXACTLY
 * (`app.LeadSource`, `app.LeadUse`, `app.LeadIntent`, `app.LeadStatus`,
 * `app.InterestStatus`). They are the typed contract the FE uses for inputs /
 * filters; the row schemas keep the field an open string per the tolerance rule.
 */

// ── Enums (must stay in lockstep with the Prisma `app.<Enum>` definitions) ──────

/** `app.LeadSource` — where a lead was captured. */
export const LEAD_SOURCES = [
  "landing",
  "ad_listing",
  "owner_portal",
  "manual",
  // Interest from the general listings page (no specific ad/listing).
  "listing_page",
] as const;
export const leadSourceEnum = z.enum(LEAD_SOURCES);
export type LeadSource = (typeof LEAD_SOURCES)[number];

/**
 * `app.LeadUse` — the property-purpose taxonomy, aligned 1:1 with the FE
 * PROPERTY_PURPOSES list (src/lib/propertyUnitMatrix.ts) used when creating a
 * property, so an interest's `use` matches what properties actually offer.
 */
export const LEAD_USES = [
  "commercial",
  "residential",
  "agricultural",
  "industrial",
  "medical",
  "educational",
  "multi_purpose",
] as const;
export const leadUseEnum = z.enum(LEAD_USES);
export type LeadUse = (typeof LEAD_USES)[number];

/** `app.LeadIntent` — combinable per Interest (buy + rent representable). */
export const LEAD_INTENTS = ["buy", "rent", "sell"] as const;
export const leadIntentEnum = z.enum(LEAD_INTENTS);
export type LeadIntent = (typeof LEAD_INTENTS)[number];

/** `app.LeadStatus` — lead lifecycle (refined alongside the Pipeline epic). */
export const LEAD_STATUSES = [
  "new",
  "profiled",
  "matched",
  "converted",
  "lost",
] as const;
export const leadStatusEnum = z.enum(LEAD_STATUSES);
export type LeadStatus = (typeof LEAD_STATUSES)[number];

/** `app.InterestStatus` — interest lifecycle. */
export const INTEREST_STATUSES = [
  "active",
  "matched",
  "fulfilled",
  "closed",
] as const;
export const interestStatusEnum = z.enum(INTEREST_STATUSES);
export type InterestStatus = (typeof INTEREST_STATUSES)[number];

/**
 * Decimal price columns (`Interest.price_min`/`price_max` are `Decimal(14,2)`)
 * come over the wire as a plain number — the Nest service serializes the Prisma
 * Decimal back to `number` on read. We still accept `string` to tolerate any
 * serializer (or driver) that emits the Decimal as a string, mirroring how the
 * shared invoices/expenses schemas model money columns
 * (`z.union([z.number(), z.string()])`). `.nullish()` per the NULLABILITY RULE.
 */
const decimalWire = z.union([z.number(), z.string()]).nullish();

// ── Lead (row) ──────────────────────────────────────────────────────────────

/**
 * A persisted lead as the staff dashboard consumes it. Field set + nullability
 * mirror the Prisma `Lead` model serialized to the snake_case wire shape by the
 * Nest `LeadsService.toDto`. `source` / `status` are kept open strings on the
 * row (tolerance rule) — use {@link leadSourceEnum} / {@link leadStatusEnum} for
 * typed input/filtering.
 */
export const leadSchema = z
  .object({
    id: z.string(),
    company_id: z.string(),
    source: z.string(),
    full_name: z.string(),
    phone: z.string(),
    national_id: z.string().nullish(),
    email: z.string().nullish(),
    status: z.string(),
    user_id: z.string().nullish(),
    created_by: z.string().nullish(),
    // Lead→Task back-link (Epic L / S8). Null until the lead spawns its kanban
    // task; carries the task id once spawned (round-trip link).
    spawned_task_id: z.string().nullish(),
    created_at: z.string(),
    updated_at: z.string(),
    deleted_at: z.string().nullish(),
  })
  .passthrough();
export type Lead = z.infer<typeof leadSchema>;

// ── LeadProfile (row) ─────────────────────────────────────────────────────────

/**
 * A persisted lead profile (segmentation / targeting enrichment) as serialized
 * from the Prisma `LeadProfile` model. Kept separate from {@link leadSchema} so
 * it can grow without bloating the core lead row.
 */
export const leadProfileSchema = z
  .object({
    id: z.string(),
    company_id: z.string(),
    lead_id: z.string(),
    tags: z.array(z.string()),
    budget_band: z.string().nullish(),
    notes: z.string().nullish(),
    created_at: z.string(),
    updated_at: z.string(),
    deleted_at: z.string().nullish(),
  })
  .passthrough();
export type LeadProfile = z.infer<typeof leadProfileSchema>;

// ── Interest (row) ────────────────────────────────────────────────────────────

/**
 * A persisted interest (a lead's demand criteria) as serialized from the Prisma
 * `Interest` model. `use` / `status` are kept open strings on the row (tolerance
 * rule); `intents` is a string array. Price columns are Decimal-on-the-wire (see
 * {@link decimalWire}).
 */
export const interestSchema = z
  .object({
    id: z.string(),
    company_id: z.string(),
    lead_id: z.string(),
    use: z.string(),
    intents: z.array(z.string()),
    city: z.string().nullish(),
    district: z.string().nullish(),
    size_min: z.number().nullish(),
    size_max: z.number().nullish(),
    radius_km: z.number().nullish(),
    price_min: decimalWire,
    price_max: decimalWire,
    rooms: z.number().nullish(),
    property_type: z.string().nullish(),
    status: z.string(),
    notes: z.string().nullish(),
    source_project_id: z.string().uuid().nullish(),
    // Interest→Task back-link (per-interest pipeline). Null until this interest
    // spawns its kanban task; carries the task id once spawned. Each interest is
    // its own board card, so staff work demand-by-demand.
    spawned_task_id: z.string().nullish(),
    created_at: z.string(),
    updated_at: z.string(),
    deleted_at: z.string().nullish(),
  })
  .passthrough();
export type Interest = z.infer<typeof interestSchema>;

// ── Input schemas (what the FE sends) ─────────────────────────────────────────

/**
 * Create payload sent to POST /leads — mirrors the Nest `CreateLeadDto`.
 * `source` / `full_name` / `phone` are required; the rest optional. company_id
 * and audit columns are server-owned (never sent). The typed enums are used here
 * (input validation can be strict — only the open-string ROW tolerates unknown
 * server values).
 */
export const createLeadInput = z.object({
  source: leadSourceEnum,
  full_name: z.string().min(1),
  phone: z.string().min(8).max(20),
  national_id: z.string().nullish(),
  email: z.string().email().nullish(),
  status: leadStatusEnum.nullish(),
});
export type CreateLeadInput = z.infer<typeof createLeadInput>;

/** Update payload sent to PATCH /leads/:id — every field optional. */
export const updateLeadInput = createLeadInput.partial();
export type UpdateLeadInput = z.infer<typeof updateLeadInput>;

/**
 * Create payload sent to POST /leads/:leadId/interests — mirrors the Nest
 * `CreateInterestDto`. `use` / `intents` (≥1, unique) / `city` are required; the
 * rest optional. company_id / lead_id / audit columns are server-owned. The
 * service additionally enforces priceMin ≤ priceMax and sizeMin ≤ sizeMax.
 */
export const createInterestInput = z.object({
  use: leadUseEnum,
  intents: z.array(leadIntentEnum).nonempty(),
  city: z.string().min(1),
  district: z.string().nullish(),
  size_min: z.number().int().min(0).nullish(),
  size_max: z.number().int().min(0).nullish(),
  radius_km: z.number().int().min(0).nullish(),
  price_min: z.number().min(0).nullish(),
  price_max: z.number().min(0).nullish(),
  rooms: z.number().int().min(0).nullish(),
  property_type: z.string().nullish(),
  status: interestStatusEnum.nullish(),
  notes: z.string().nullish(),
});
export type CreateInterestInput = z.infer<typeof createInterestInput>;

/** Update payload sent to PATCH /leads/:leadId/interests/:id — all optional. */
export const updateInterestInput = createInterestInput.partial();
export type UpdateInterestInput = z.infer<typeof updateInterestInput>;

// ── Anonymous public capture (Epic L / S5) ────────────────────────────────────

/**
 * Public anonymous interest capture — sent to POST /leads/public from a public ad
 * listing page by an UNAUTHENTICATED visitor (no JWT). The `company_id` is NOT
 * sent: the server derives it from the listing's owning company (multi-tenant
 * isolation without auth — AC3). `listing_id` resolves the company; `phone` +
 * optional `full_name` are the contact; the interest criteria mirror
 * {@link createInterestInput} (use / intents required, the rest optional and
 * typically pre-filled from the listing — city / property_type / price).
 *
 * The server dedupes by (company_id, phone): re-submitting the same phone reuses
 * the existing lead and just attaches the new interest (AC2).
 */
export const publicInterestCaptureInput = z.object({
  // Exactly ONE source — `listing_id` (ad listing) OR `project_id` (off-plan
  // project). The server derives company_id from whichever is given and enforces
  // the "exactly one" rule (LeadsService.captureFromListing).
  listing_id: z.string().uuid().optional(),
  project_id: z.string().uuid().optional(),
  full_name: z.string().min(1).max(200).nullish(),
  phone: z.string().min(8).max(20),
  // OTP proving phone ownership — verified + consumed server-side before any
  // write (the visitor stays logged out). REQUIRED for listing / general-page
  // captures (the server enforces it); OPTIONAL on the wire so project-sourced
  // bookings (which don't run the OTP step) and older bundles never fail Zod parse.
  code: z.string().length(6).optional(),
  use: leadUseEnum,
  intents: z.array(leadIntentEnum).nonempty(),
  // Required for listing-sourced captures; optional for project-sourced ones
  // (the service derives city from the project row instead). The server
  // enforces the "listing requires city" rule via ConditionalValidate on the
  // DTO — the shared schema is permissive here so old bundles that omit city
  // on project-sourced captures never fail Zod parse.
  city: z.string().min(1).optional(),
  property_type: z.string().nullish(),
  price_min: z.number().min(0).nullish(),
  price_max: z.number().min(0).nullish(),
  notes: z.string().nullish(),
});
export type PublicInterestCaptureInput = z.infer<
  typeof publicInterestCaptureInput
>;

/**
 * Minimal confirmation returned by POST /leads/public. Deliberately NOT the full
 * Lead/Interest row — an anonymous caller must not be able to read back other
 * tenants' data (or even confirm the full lead state). Just the created ids +
 * whether the lead was reused (dedupe hit) so the FE can show a clean toast.
 */
export const publicInterestCaptureResult = z.object({
  lead_id: z.string(),
  interest_id: z.string(),
  deduped: z.boolean(),
});
export type PublicInterestCaptureResult = z.infer<
  typeof publicInterestCaptureResult
>;

// ── Lead-by-task lookup (Task modal "Lead details" section) ───────────────────

/**
 * Minimal display info for the ad listing an anonymous visitor registered
 * interest on (server-resolved from `Interest.source_listing_id`, company-scoped
 * via `ListingsRepository.getDisplayInfoById`). Lets the Task modal label WHICH
 * property an external visitor was looking at. `null` on the interest when there
 * is no source listing (or it was deleted / is cross-tenant — best-effort
 * enrichment degrades gracefully). `.passthrough()` + `.nullish()` per the file
 * conventions.
 */
export const leadSourceListingSchema = z
  .object({
    id: z.string(),
    title: z.string().nullish(),
    title_en: z.string().nullish(),
    property_name: z.string().nullish(),
    property_name_en: z.string().nullish(),
  })
  .passthrough();
export type LeadSourceListing = z.infer<typeof leadSourceListingSchema>;

/**
 * An interest as returned by GET /leads/by-task/:taskId — the standard
 * {@link interestSchema} row enriched with the resolved `source_listing` (or
 * null). The Task modal renders the interest criteria + the source-listing line.
 */
export const interestWithSourceListingSchema = interestSchema.extend({
  source_listing: leadSourceListingSchema.nullish(),
  // The interest's spawned kanban task status (or null) — the FE maps it to a
  // pipeline stage so the owner can TRACK this specific request. Populated by
  // GET /leads/mine; omitted (nullish) on the by-task modal path.
  task_status: z.string().nullish(),
});
export type InterestWithSourceListing = z.infer<
  typeof interestWithSourceListingSchema
>;

/**
 * Result of GET /leads/by-task/:taskId — the lead that spawned a given kanban
 * task plus its interests (each with the resolved source listing). Used by the
 * Task modal's "Lead details" section. A task with no linked lead is a 404 (the
 * FE treats absence as "not a lead-spawned task" and renders nothing).
 *
 * `lead` is a deliberate SUBSET of {@link leadSchema} (the display fields the
 * section needs) — `.passthrough()` so additional server columns never reject.
 * `source`/`status` stay open strings (tolerance rule).
 */
export const leadByTaskResult = z.object({
  lead: z
    .object({
      id: z.string(),
      source: z.string(),
      full_name: z.string(),
      phone: z.string(),
      user_id: z.string().nullish(),
      status: z.string(),
    })
    .passthrough(),
  interests: z.array(interestWithSourceListingSchema),
});
export type LeadByTaskResult = z.infer<typeof leadByTaskResult>;

// ── Owner booking (authenticated — POST /leads/owner-booking) ─────────────────

/**
 * Authenticated owner booking of an off-plan project unit. Sent from
 * `/portal/owner/opportunities` — the owner is known (JWT), so we link the
 * lead to the owner's userId (unlike the anonymous public capture path).
 *
 * `company_id` is derived server-side from the project (multi-tenant isolation
 * without trusting the body). `full_name` / `phone` are pre-filled from the
 * owner's profile on the FE. `intents` is always ['buy'] — a booking implies
 * purchase intent; the FE should not send it.
 */
export const ownerBookingInput = z.object({
  project_id: z.string().uuid(),
  full_name: z.string().min(1).max(200),
  phone: z.string().min(8).max(20),
  unit_type: z.string().min(1).optional(),
  message: z.string().optional(),
});
export type OwnerBookingInput = z.infer<typeof ownerBookingInput>;

/**
 * Result of POST /leads/owner-booking — mirrors {@link publicInterestCaptureResult}
 * with the same three fields so the FE can handle both capture paths uniformly.
 */
export const ownerBookingResult = z.object({
  lead_id: z.string(),
  interest_id: z.string(),
  deduped: z.boolean(),
});
export type OwnerBookingResult = z.infer<typeof ownerBookingResult>;

// ── Owner "My Interests" (GET /leads/mine) ────────────────────────────────────

/**
 * Result of GET /leads/mine — the AUTHENTICATED user's own leads + interests
 * (scoped by Lead.userId, not company), each interest enriched with its source
 * listing. Powers the Owner Portal "My Interests" history table. Same element
 * shape as {@link leadByTaskResult} plus the lead's `source` + `created_at` so
 * the table can show where/when each request was registered.
 */
export const myInterestRow = z.object({
  lead: z
    .object({
      id: z.string(),
      source: z.string(),
      full_name: z.string(),
      phone: z.string(),
      status: z.string(),
      // The auth.users link (or null for an anonymous/unclaimed lead) — drives
      // the anonymous-vs-registered distinction in LeadDetailsSection. Without
      // it every lead would fall back to "anonymous" (!undefined === true).
      user_id: z.string().nullish(),
      created_at: z.string(),
      // The spawned kanban task's raw status (or null) — the FE maps it to a
      // pipeline stage so the owner can TRACK where their request stands.
      task_status: z.string().nullish(),
    })
    .passthrough(),
  interests: z.array(interestWithSourceListingSchema),
});
export const myInterestsResult = z.array(myInterestRow);
export type MyInterestRow = z.infer<typeof myInterestRow>;
export type MyInterestsResult = z.infer<typeof myInterestsResult>;
